// Pages/inventario/components/InventarioFinanzasTab.tsx
// Pestaña "Finanzas" de /inventario (round 2026-07-26).
//
// Decisión del owner: TODA la plata relacionada con inventario vive en la
// sección Inventario — no se mezcla con /flujos (los flujos derivados se
// excluyeron de ahí). Esta vista concentra, por MES:
//   - Resumen: compras (egreso), cobros a clientes (ingreso), reembolsos,
//     neto — y el DESGLOSE POR CATEGORÍA (la razón de ser de la categoría
//     del producto: entender qué pasó con la plata de cada rubro).
//   - Transacciones: cada compra/movimiento, con corrección de tipeos
//     (modal con alerta de impacto: flujos vinculados + mes cerrado).
//   - Cobros a clientes: recuperaciones con monto editable (parcial / $0 =
//     regalo / mayor al costo).
//   - Reembolsos: adelantos de terceros, monto editable (re-pende si estaba
//     pagado).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  HistoryOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { CADUCIDAD_FECHAS_DIAS } from "../../../hooks/filtrosPersistidos";
import { useFiltroPersistido } from "../../../hooks/useFiltroPersistido";
import { apiFetch } from "../../../services/apiClient";
import { inventarioApi } from "../../../services/inventario.api";
import HistoriaProductoDrawer from "./HistoriaProductoDrawer";
import { CATEGORIA_LABEL } from "../../flujos/flujos.api";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import { getMsg } from "../../../utils/antdStatic";
import { ahoraChile } from "../../../utils/fechaChile";
// Import explícito: los códigos (`.inventario-id-tag`) se usan acá y en la
// tabla de Productos. Hoy este componente solo se monta dentro de
// `inventario.tsx`, que ya carga la hoja, pero depender de eso lo dejaría sin
// estilos el día que se use en otro lado. Los imports de CSS son idempotentes.
import "../inventario.css";

import { paginacionCliente } from "../../../utils/tablaPaginacion";
const { Text } = Typography;

const CLP = (n: number | null | undefined) =>
  `$${Number(n || 0).toLocaleString("es-CL")}`;

// ─── Tipos del payload de GET /inventario/finanzas ──────────────────────────

interface TxRow {
  id: number;
  tipo: "compra" | "movimiento";
  fecha: string;
  productoNombre: string;
  categoria: string | null;
  inventarioId: number | null;
  cantidadUnidades: number;
  esPack: boolean;
  cantidadPacks: number;
  unidadesPorPack: number;
  precioPorUnidad: number;
  precioPorPack: number;
  montoTotal: number;
  origenTipo: string | null;
  origenNombre: string | null;
  destinoTipo: string;
  destinoNombre: string | null;
  registradoPor: string | null;
  /**
   * El producto de esta transacción YA NO EXISTE en `inventario`.
   *
   * Pasa, sobre todo, cuando un movimiento vació la ubicación de origen: la
   * fila del producto se destruía y su compra quedaba colgando. La plata
   * sigue contando en "Compras del mes", pero la fila no aparece en la vista
   * Productos y el borrado del producto no la alcanza.
   */
  huerfana?: boolean;
}

interface FlujoRow {
  id: number;
  inventoryTxId: number;
  monto: number;
  fecha: string;
  estadoPago: string | null;
  descripcion: string | null;
  productoNombre: string | null;
  categoria: string | null;
  costoTransaccion: number | null;
  propiedadNombre: string | null;
  contraparteNombre: string | null;
}

interface FinanzasData {
  mes: string;
  transacciones: TxRow[];
  recuperaciones: FlujoRow[];
  reembolsos: FlujoRow[];
  resumen: {
    totalCompras: number;
    totalMovimientos: number;
    totalRecuperaciones: number;
    totalReembolsos: number;
    reembolsosPendientes: number;
    neto: number;
  };
  porCategoria: Array<{
    categoria: string | null;
    compras: number;
    cobros: number;
    neto: number;
  }>;
}

const catLabel = (c: string | null) =>
  c ? (CATEGORIA_LABEL[c] ?? c) : "(sin categoría)";

// ─── Modal de corrección de transacción ─────────────────────────────────────

const TxEditModal: React.FC<{
  tx: TxRow | null;
  onClose: (recargar: boolean) => void;
}> = ({ tx, onClose }) => {
  const [impacto, setImpacto] = useState<any | null>(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [vals, setVals] = useState<{
    cantidad_unidades: number;
    es_pack: boolean;
    cantidad_packs: number;
    unidades_por_pack: number;
    precio_por_unidad: number;
    precio_por_pack: number;
  } | null>(null);

  useEffect(() => {
    if (!tx) {
      setImpacto(null);
      setVals(null);
      return;
    }
    setVals({
      cantidad_unidades: tx.cantidadUnidades,
      es_pack: tx.esPack,
      cantidad_packs: tx.cantidadPacks,
      unidades_por_pack: tx.unidadesPorPack,
      precio_por_unidad: tx.precioPorUnidad,
      precio_por_pack: tx.precioPorPack,
    });
    setCargando(true);
    inventarioApi
      .getTransaccionImpacto(tx.id)
      .then((r) => setImpacto(r?.data ?? null))
      .finally(() => setCargando(false));
  }, [tx]);

  // Movimiento interno (bodega/bodeguita ↔ bodega/bodeguita): su monto es
  // SIEMPRE $0 por GEOMETRÍA — mover algo entre depósitos no es plata nueva.
  // El backend fuerza los precios a 0 antes de recalcular, así que el preview
  // tiene que hacer lo mismo (si no, mostraba un monto que el server no iba a
  // guardar). Los campos de precio se deshabilitan en ese caso.
  const esMovimientoInterno =
    tx?.tipo === "movimiento" &&
    (tx?.origenTipo === "bodegas" || tx?.origenTipo === "bodeguitas") &&
    (tx?.destinoTipo === "bodegas" || tx?.destinoTipo === "bodeguitas");

  // Preview del monto — espejo de calcularMonto del backend (el server
  // recalcula igual; esto es solo display).
  const montoPreview = useMemo(() => {
    if (!vals) return 0;
    if (esMovimientoInterno) return 0;
    // EXCLUSIVO, no aditivo: el precio por pack y el por unidad son la misma
    // plata expresada de dos formas (espejo de calcularMonto del backend).
    if (vals.es_pack && vals.cantidad_packs > 0 && vals.precio_por_pack > 0) {
      return vals.cantidad_packs * vals.precio_por_pack;
    }
    if (vals.cantidad_unidades > 0 && vals.precio_por_unidad > 0) {
      return vals.cantidad_unidades * vals.precio_por_unidad;
    }
    return 0;
  }, [vals, esMovimientoInterno]);

  if (!tx || !vals) return null;

  const flujos: any[] = impacto?.flujos ?? [];
  const cobro = flujos.find((f) => f.rol === "recibe");
  const reembolso = flujos.find((f) => f.rol === "paga");

  const guardar = async () => {
    setGuardando(true);
    try {
      const resp = await inventarioApi.updateTransaccion(tx.id, vals);
      if (resp?.success) {
        getMsg()?.success(resp.message || "Transacción corregida");
        onClose(true);
      }
    } finally {
      setGuardando(false);
    }
  };

  const num = (v: number | null) => Math.max(0, Math.round(Number(v ?? 0)));

  return (
    <Modal
      open
      title={`Corregir transacción #${tx.id} — ${tx.productoNombre}`}
      onCancel={() => onClose(false)}
      footer={[
        <Button key="c" onClick={() => onClose(false)} disabled={guardando}>
          Cancelar
        </Button>,
        <Popconfirm
          key="s"
          title="¿Confirmas la corrección?"
          description="Esto modifica un registro financiero histórico y actualiza las finanzas vinculadas."
          okText="Sí, corregir"
          cancelText="No"
          onConfirm={guardar}
        >
          <Button type="primary" danger loading={guardando}>
            Guardar corrección
          </Button>
        </Popconfirm>,
      ]}
      width={560}
    >
      {cargando ? (
        <Spin />
      ) : (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Estás corrigiendo un registro financiero histórico"
            description={
              <div>
                <div>
                  Fecha original: <Text strong>{tx.fecha}</Text> (no editable —
                  el gasto figura el día que ocurrió). Tampoco se puede cambiar
                  origen/destino: para eso elimina y recrea.
                </div>
                {cobro && (
                  <div style={{ marginTop: 4 }}>
                    • Cobro al cliente vinculado ({CLP(cobro.monto)}
                    {cobro.contraparte ? ` — ${cobro.contraparte}` : ""}):{" "}
                    {Number(cobro.monto) === Number(tx.montoTotal)
                      ? "se sincronizará al monto corregido."
                      : "tiene monto EDITADO A MANO — se respetará tal cual."}
                  </div>
                )}
                {reembolso &&
                  (() => {
                    // Espejo de computeTxEditCascade: el reembolso sigue la
                    // corrección SOLO si su monto deriva 1:1 de esta transacción.
                    // Si no (editado a mano, o del bot cubriendo varios productos
                    // de un envío), el backend lo RESPETA — prometer lo contrario
                    // hacía que el admin creyera haber ajustado una deuda que
                    // quedó intacta.
                    const deriva1a1 = Number(reembolso.monto) === Number(tx.montoTotal);
                    const cambiaMonto = montoPreview !== Number(tx.montoTotal);
                    return (
                      <div style={{ marginTop: 4 }}>
                        • Reembolso vinculado ({CLP(reembolso.monto)}
                        {reembolso.contraparte ? ` — a ${reembolso.contraparte}` : ""}
                        ):{" "}
                        {!deriva1a1
                          ? "su monto NO deriva de esta transacción (editado a mano, o cubre varios productos de un envío del bot) — se respetará tal cual."
                          : cambiaMonto
                            ? reembolso.estadoPago === "pagado"
                              ? "seguirá el monto corregido y VOLVERÁ A PENDIENTE (re-aprobación)."
                              : "seguirá el monto corregido."
                            : "no cambia (el monto total queda igual)."}
                      </div>
                    );
                  })()}
                {impacto?.mesCerrado && (
                  <div style={{ marginTop: 4 }}>
                    ⚠️ <Text strong>El mes {impacto.periodo} ya está CERRADO</Text>{" "}
                    para el cliente
                    {impacto.propiedadNombre
                      ? ` de ${formatPropertyName(impacto.propiedadNombre)}`
                      : ""}
                    : sus números publicados cambiarán retroactivamente.
                  </div>
                )}
                <div style={{ marginTop: 4 }}>
                  El stock del producto NO cambia con esta corrección — si
                  también estaba mal, edítalo aparte en la pestaña Productos.
                </div>
              </div>
            }
          />
          <Row gutter={12}>
            <Col span={12}>
              <label>Es pack</label>
              <div>
                <Switch
                  checked={vals.es_pack}
                  onChange={(v) => setVals({ ...vals, es_pack: v })}
                />
              </div>
            </Col>
            <Col span={12}>
              <label>Cantidad de unidades</label>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                value={vals.cantidad_unidades}
                onChange={(v) => setVals({ ...vals, cantidad_unidades: num(v) })}
              />
            </Col>
          </Row>
          {vals.es_pack && (
            <Row gutter={12} style={{ marginTop: 8 }}>
              <Col span={12}>
                <label>Cantidad de packs</label>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  value={vals.cantidad_packs}
                  onChange={(v) => setVals({ ...vals, cantidad_packs: num(v) })}
                />
              </Col>
              <Col span={12}>
                <label>Unidades por pack</label>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  value={vals.unidades_por_pack}
                  onChange={(v) => setVals({ ...vals, unidades_por_pack: num(v) })}
                />
              </Col>
            </Row>
          )}
          <Row gutter={12} style={{ marginTop: 8 }}>
            <Col span={12}>
              <label>Precio por unidad (CLP)</label>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                disabled={esMovimientoInterno}
                value={esMovimientoInterno ? 0 : vals.precio_por_unidad}
                onChange={(v) => setVals({ ...vals, precio_por_unidad: num(v) })}
              />
            </Col>
            {vals.es_pack && (
              <Col span={12}>
                <label>Precio por pack (CLP)</label>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  disabled={esMovimientoInterno}
                  value={esMovimientoInterno ? 0 : vals.precio_por_pack}
                  onChange={(v) => setVals({ ...vals, precio_por_pack: num(v) })}
                />
              </Col>
            )}
          </Row>
          {esMovimientoInterno && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8 }}
              message="Movimiento entre depósitos: el monto es siempre $0"
              description="Mover un producto entre bodegas o bodeguitas no genera plata nueva — el gasto ya quedó registrado en la compra original, con su fecha y su precio. Por eso los precios quedan bloqueados en $0: solo puedes corregir las cantidades."
            />
          )}
          <div style={{ marginTop: 12 }}>
            <Text>
              Monto resultante:{" "}
              <Text strong>
                {CLP(montoPreview)}{" "}
                {montoPreview !== tx.montoTotal && (
                  <Text type="secondary">(antes {CLP(tx.montoTotal)})</Text>
                )}
              </Text>
            </Text>
          </div>
        </>
      )}
    </Modal>
  );
};

// ─── Modal de ELIMINACIÓN de transacción ────────────────────────────────────
//
// Round 2026-08-06. Antes no existía ninguna forma de borrar una transacción:
// el único borrado del módulo parte del PRODUCTO, así que una transacción cuyo
// producto ya no existe quedaba fuera de alcance para siempre mientras su monto
// seguía sumando en "Compras del mes".
//
// Misma UX de doble confirmación que DeleteProductModal: impacto explícito
// primero, y el botón rojo solo se habilita al escribir el nombre del producto.
// Si el impacto NO se pudo calcular, el borrado queda bloqueado — borrar a
// ciegas plata vinculada es peor que no borrar.

const TxDeleteModal: React.FC<{
  tx: TxRow | null;
  onClose: (recargar: boolean) => void;
}> = ({ tx, onClose }) => {
  const [impacto, setImpacto] = useState<any | null>(null);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [confirmacion, setConfirmacion] = useState("");

  useEffect(() => {
    if (!tx) return;
    setImpacto(null);
    setFallo(false);
    setConfirmacion("");
    setCargando(true);
    void inventarioApi
      .getTransaccionImpacto(tx.id)
      .then((r) => {
        if (r?.data) setImpacto(r.data);
        else setFallo(true);
      })
      .finally(() => setCargando(false));
  }, [tx]);

  if (!tx) return null;

  const flujos: any[] = impacto?.flujos ?? [];
  const cobros = flujos.filter((f) => f.rol === "recibe");
  const reembolsos = flujos.filter((f) => f.rol === "paga");
  const pagados = reembolsos.filter((f) => f.estadoPago === "pagado");
  const esperado = (tx.productoNombre || "").trim();
  const puedeBorrar =
    !cargando && !fallo && !borrando &&
    confirmacion.trim().toLowerCase() === esperado.toLowerCase();

  const borrar = async () => {
    setBorrando(true);
    try {
      const r = await inventarioApi.deleteTransaccion(tx.id);
      if (r) {
        getMsg()?.success(r.message ?? "Transacción eliminada");
        onClose(true);
      }
    } finally {
      setBorrando(false);
    }
  };

  return (
    <Modal
      open
      title={`Eliminar transacción #${tx.id}`}
      onCancel={() => onClose(false)}
      okText="Eliminar definitivamente"
      cancelText="Cancelar"
      okButtonProps={{ danger: true, disabled: !puedeBorrar, loading: borrando }}
      onOk={borrar}
      width={640}
      destroyOnClose
    >
      {cargando && <Spin />}
      {fallo && (
        <Alert
          type="error"
          showIcon
          message="No se pudo calcular el impacto"
          description="No podemos saber qué cobros o reembolsos quedarían colgando, así que el borrado está bloqueado. Reintenta en un momento."
        />
      )}
      {!cargando && !fallo && impacto && (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Esto borra un hecho histórico, no lo corrige"
            description={
              <>
                Se eliminarán <strong>{CLP(tx.montoTotal)}</strong> de las compras
                de <strong>{String(tx.fecha).slice(0, 7)}</strong> y la utilidad
                del período se recalcula. Si lo que quieres es arreglar un error
                de tipeo en cantidades o precios, usa el lápiz en vez de esto.
              </>
            }
          />

          {impacto.productoExiste === false ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Esta transacción quedó sin producto"
              description="Su producto ya no existe en el inventario, por eso no aparece en la vista Productos ni se puede borrar desde ahí. Antes de eliminarla, ten presente que si la mercadería se movió a otra ubicación, esta fila puede ser el único registro de lo que costó."
            />
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="El producto sigue existiendo"
              description="Su STOCK no se va a ajustar: si esta compra nunca ocurrió, corrige también las cantidades editando el producto."
            />
          )}

          <div style={{ marginBottom: 12 }}>
            <Text strong>Se borrará junto con la transacción:</Text>
            {flujos.length === 0 ? (
              <div>
                <Text type="secondary">
                  Ningún flujo de dinero vinculado (no hay cobro al cliente ni
                  reembolso a terceros).
                </Text>
              </div>
            ) : (
              <ul style={{ marginTop: 6, marginBottom: 0 }}>
                {cobros.map((f) => (
                  <li key={f.id}>
                    Cobro al cliente por <strong>{CLP(f.monto)}</strong>
                    {f.contraparte ? ` (${f.contraparte})` : ""}
                  </li>
                ))}
                {reembolsos.map((f) => (
                  <li key={f.id}>
                    Reembolso por <strong>{CLP(f.monto)}</strong>
                    {f.contraparte ? ` a ${f.contraparte}` : ""}{" "}
                    <Tag color={f.estadoPago === "pagado" ? "green" : "orange"}>
                      {f.estadoPago === "pagado" ? "ya pagado" : "pendiente"}
                    </Tag>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {pagados.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="Hay un reembolso YA PAGADO"
              description="Esa plata se entregó de verdad. Al borrarlo desaparece el comprobante y el ledger de esa persona deja de cuadrar con lo que recibió."
            />
          )}

          {impacto.mesCerrado && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${impacto.periodo} ya está cerrado para el cliente`}
              description={`El cliente${impacto.propiedadNombre ? ` de ${formatPropertyName(impacto.propiedadNombre)}` : ""} ya vio estos números. Borrar ahora reescribe un histórico publicado.`}
            />
          )}

          <div>
            <Text>
              Para confirmar, escribe el nombre del producto:{" "}
              <Text code>{esperado}</Text>
            </Text>
            <Input
              style={{ marginTop: 6 }}
              value={confirmacion}
              onChange={(e) => setConfirmacion(e.target.value)}
              placeholder={esperado}
              onPressEnter={() => {
                if (puedeBorrar) void borrar();
              }}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

// ─── Modal de edición de monto (cobro / reembolso) ──────────────────────────

const MontoEditModal: React.FC<{
  flujo: (FlujoRow & { esCobro: boolean }) | null;
  onClose: (recargar: boolean) => void;
}> = ({ flujo, onClose }) => {
  const [monto, setMonto] = useState<number>(0);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (flujo) setMonto(flujo.monto);
  }, [flujo]);

  if (!flujo) return null;
  const esCobro = flujo.esCobro;

  const guardar = async () => {
    setGuardando(true);
    try {
      const resp = await apiFetch<any>(`/finanzas/empresa/expenses/${flujo.id}`, {
        method: "PUT",
        body: JSON.stringify({ monto }),
        errorMsg: "Error al actualizar el monto",
      });
      if (resp) {
        getMsg()?.success(
          esCobro
            ? "Cobro al cliente actualizado"
            : "Reembolso actualizado" +
                (flujo.estadoPago === "pagado" && monto !== flujo.monto
                  ? " (volvió a PENDIENTE — requiere re-aprobación)"
                  : ""),
        );
        onClose(true);
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open
      title={
        esCobro
          ? `Editar cobro al cliente — ${flujo.productoNombre ?? ""}`
          : `Editar reembolso — ${flujo.productoNombre ?? ""}`
      }
      onCancel={() => onClose(false)}
      footer={[
        <Button key="c" onClick={() => onClose(false)} disabled={guardando}>
          Cancelar
        </Button>,
        <Popconfirm
          key="s"
          title="¿Confirmas el nuevo monto?"
          description={
            esCobro
              ? `Cambiará lo que se le cobra al cliente${flujo.propiedadNombre ? ` de ${formatPropertyName(flujo.propiedadNombre)}` : ""} por este producto (fecha ${flujo.fecha}).`
              : `Cambiará lo que AF le debe a ${flujo.contraparteNombre ?? "el tercero"}${flujo.estadoPago === "pagado" ? " y el reembolso VOLVERÁ A PENDIENTE (ya estaba pagado)" : ""}.`
          }
          okText="Sí, guardar"
          cancelText="No"
          onConfirm={guardar}
        >
          <Button type="primary" loading={guardando}>
            Guardar
          </Button>
        </Popconfirm>,
      ]}
      width={480}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          esCobro
            ? "El cobro es independiente del costo"
            : "El reembolso es lo que AF le debe al tercero"
        }
        description={
          esCobro ? (
            <>
              El costo de la compra ({CLP(flujo.costoTransaccion)}) NO cambia —
              vive en la transacción. Puedes cobrar el costo, un parcial, más
              (ganancia) o <Text strong>$0 (regalo)</Text>. El resultado
              (ganancia/pérdida/neteo) sale de la resta cobro − costo.
            </>
          ) : (
            <>
              Corrígelo solo si el monto adelantado real fue otro. Si ya estaba
              pagado, volverá a pendiente para re-aprobación.
            </>
          )
        }
      />
      <label>Monto (CLP)</label>
      <InputNumber
        min={esCobro ? 0 : 1}
        style={{ width: "100%" }}
        value={monto}
        onChange={(v) => setMonto(Math.max(0, Math.round(Number(v ?? 0))))}
      />
    </Modal>
  );
};

// ─── Vista principal ─────────────────────────────────────────────────────────

/** Un mes guardado se escribe "YYYY-MM": Dayjs no sobrevive a JSON. */
const ES_MES = /^\d{4}-\d{2}$/;

const InventarioFinanzasTab: React.FC = () => {
  // El mes se recuerda, pero CADUCA a los 7 días como cualquier filtro de
  // fecha: el panel es mensual y no tiene "quitar el filtro" (allowClear está
  // apagado), así que entrar el 2 de octubre a las cifras de agosto se leería
  // como que este mes no tuvo movimientos.
  const [mes, setMes] = useFiltroPersistido<Dayjs>(
    "inventario.finanzasMes",
    () => ahoraChile(),
    {
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      serializar: (m) =>
        m && dayjs.isDayjs(m) && m.isValid() ? m.format("YYYY-MM") : null,
      deserializar: (crudo) => {
        if (typeof crudo !== "string" || !ES_MES.test(crudo)) return undefined;
        const d = dayjs(`${crudo}-01`);
        // `isValid()` no alcanza: dayjs("2026-13-01") no falla, se corre a 2027.
        return d.isValid() && d.format("YYYY-MM") === crudo ? d : undefined;
      },
    },
  );
  const [data, setData] = useState<FinanzasData | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqSeqRef = useRef(0);
  // `cargar` se define más abajo pero varias acciones necesitan re-cargar el
  // panel; la ref evita reordenar el archivo o arrastrar la dependencia por
  // todos los useCallback (mismo patrón que `refetchRef` en useInventarioLogic).
  const cargarRef = useRef<null | (() => Promise<void>)>(null);
  const [txEdit, setTxEdit] = useState<TxRow | null>(null);
  const [txDelete, setTxDelete] = useState<TxRow | null>(null);
  const [flujoEdit, setFlujoEdit] = useState<(FlujoRow & { esCobro: boolean }) | null>(
    null,
  );
  // Buscador global de transacciones sin producto. El panel es MENSUAL, así
  // que sin esto hay que adivinar en qué mes quedó cada una.
  const [huerfanas, setHuerfanas] = useState<any | null>(null);
  const [buscandoHuerfanas, setBuscandoHuerfanas] = useState(false);
  // Producto cuya línea de tiempo se está mirando (round 2026-08-06).
  const [historia, setHistoria] = useState<{
    id: number;
    nombre: string | null;
  } | null>(null);

  const buscarHuerfanas = useCallback(async () => {
    setBuscandoHuerfanas(true);
    try {
      const r = await inventarioApi.getTransaccionesHuerfanas();
      if (r?.data) setHuerfanas(r.data);
    } finally {
      setBuscandoHuerfanas(false);
    }
  }, []);

  /**
   * Borra un cobro/reembolso cuya transacción de inventario ya no existe.
   *
   * Recarga las dos vistas: la lista de huérfanas (de donde salió la fila) y el
   * panel del mes, porque el flujo también estaba sumando ahí.
   */
  const eliminarFlujoHuerfano = useCallback(
    async (id: number) => {
      const r = await inventarioApi.deleteFlujoHuerfano(id);
      if (r?.success) {
        await buscarHuerfanas();
        await cargarRef.current?.();
      }
    },
    [buscarHuerfanas],
  );

  const cargar = useCallback(async () => {
    const solicitado = mes.format("YYYY-MM");
    // Secuencia de request: descarta CUALQUIER respuesta que ya no sea la
    // vigente, incluida la de error (un fallo devuelve null, sin `mes`, así
    // que compararlo con `data.mes` no alcanzaba).
    const seq = ++reqSeqRef.current;
    setCargando(true);
    try {
      const r = await apiFetch<any>(`/inventario/finanzas?mes=${solicitado}`, {
        errorMsg: "Error al cargar las finanzas de inventario",
      });
      if (seq !== reqSeqRef.current) return; // llegó tarde: ya se pidió otro mes
      if (!r?.data) {
        // NO se borran los datos previos ni se pintan ceros: un $0 con
        // "Sin transacciones este mes" se lee como un hecho, cuando en
        // realidad no sabemos nada. Se marca el error y se ofrece reintentar.
        setError(`No se pudieron cargar las finanzas de ${solicitado}.`);
        return;
      }
      if (r.data.mes && r.data.mes !== solicitado) return; // respuesta obsoleta
      setError(null);
      setData(r.data);
    } finally {
      if (seq === reqSeqRef.current) setCargando(false);
    }
  }, [mes]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Mantiene la ref apuntando siempre a la última versión de `cargar`.
  useEffect(() => {
    cargarRef.current = cargar;
  }, [cargar]);

  const resumen = data?.resumen;

  const colsTx = [
    {
      // Round 2026-08-06 — antes ni la transacción ni su producto mostraban su
      // id, así que no había forma de decir de qué producto era un movimiento
      // ni de referirse a una fila concreta. Los dos códigos van juntos y en la
      // primera columna: el de arriba identifica la transacción, el de abajo el
      // producto al que pertenece.
      title: "Código",
      key: "codigos",
      width: 118,
      render: (_: any, r: TxRow) => (
        <Space direction="vertical" size={0}>
          <Tooltip title="Código de la transacción">
            <Tag color="default" className="inventario-id-tag">
              TX #{r.id}
            </Tag>
          </Tooltip>
          {r.inventarioId != null ? (
            <Tooltip title={r.huerfana ? "Producto ya eliminado — la transacción quedó sin dueño" : "Producto al que pertenece esta transacción"}>
              <Tag color={r.huerfana ? "red" : "blue"} className="inventario-id-tag">
                PROD #{r.inventarioId}
              </Tag>
            </Tooltip>
          ) : (
            <Tooltip title="Esta transacción nunca tuvo producto asociado">
              <Tag color="red" className="inventario-id-tag">
                sin producto
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: "Fecha", dataIndex: "fecha", width: 105 },
    {
      title: "Tipo",
      dataIndex: "tipo",
      width: 110,
      render: (t: string) =>
        t === "compra" ? <Tag color="volcano">Compra</Tag> : <Tag color="blue">Movimiento</Tag>,
    },
    {
      title: "Producto",
      dataIndex: "productoNombre",
      render: (n: string, r: TxRow) => (
        <>
          {n}
          {r.huerfana && (
            <Tooltip title="El producto de esta transacción ya no existe en el inventario. La fila sigue contando como gasto del mes, pero no aparece en la vista Productos y no se puede borrar desde ahí — usa la papelera de esta misma fila.">
              <Tag color="red" style={{ marginLeft: 8 }}>
                sin producto
              </Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      title: "Categoría",
      dataIndex: "categoria",
      width: 130,
      render: (c: string | null) =>
        c ? <Tag color="geekblue">{catLabel(c)}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: "Origen → Destino",
      key: "ruta",
      render: (_: any, r: TxRow) => (
        <Text style={{ fontSize: 12 }}>
          {r.origenNombre || r.origenTipo || "—"} →{" "}
          {r.destinoNombre ? formatPropertyName(r.destinoNombre) : r.destinoTipo}
        </Text>
      ),
    },
    {
      title: "Cantidades",
      key: "cant",
      width: 120,
      render: (_: any, r: TxRow) =>
        r.esPack
          ? `${r.cantidadPacks} pack(s) / ${r.cantidadUnidades} u.`
          : `${r.cantidadUnidades} u.`,
    },
    {
      title: "Monto",
      dataIndex: "montoTotal",
      width: 120,
      align: "right" as const,
      render: (m: number) => <Text strong>{CLP(m)}</Text>,
    },
    {
      title: "",
      key: "acciones",
      width: 130,
      render: (_: any, r: TxRow) => (
        <Space size={4}>
          {/* Línea de tiempo del producto (round 2026-08-06): desde acá se ve
              de dónde vino la mercadería y a dónde fue, que es lo que hace
              decidible si una corrección o un borrado tienen sentido. */}
          <Tooltip title="Ver la historia de este producto">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              disabled={r.inventarioId == null}
              onClick={() =>
                setHistoria({
                  id: Number(r.inventarioId),
                  nombre: r.productoNombre,
                })
              }
            />
          </Tooltip>
          <Tooltip title="Corregir esta transacción (error de tipeo en cantidades/precios)">
            <Button size="small" icon={<EditOutlined />} onClick={() => setTxEdit(r)} />
          </Tooltip>
          <Tooltip title="Eliminar esta transacción y sus flujos de dinero vinculados. Para cuando la compra nunca ocurrió, o quedó sin producto.">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => setTxDelete(r)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const colsFlujo = (esCobro: boolean) => [
    { title: "Fecha", dataIndex: "fecha", width: 105 },
    { title: "Producto", dataIndex: "productoNombre" },
    {
      title: "Categoría",
      dataIndex: "categoria",
      width: 130,
      render: (c: string | null) =>
        c ? <Tag color="geekblue">{catLabel(c)}</Tag> : <Text type="secondary">—</Text>,
    },
    esCobro
      ? {
          title: "Propiedad / Cliente",
          key: "prop",
          render: (_: any, r: FlujoRow) => (
            <span>
              {r.propiedadNombre ? formatPropertyName(r.propiedadNombre) : "—"}
              {r.contraparteNombre && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {" "}
                  — {r.contraparteNombre}
                </Text>
              )}
            </span>
          ),
        }
      : {
          title: "Tercero",
          dataIndex: "contraparteNombre",
          render: (n: string | null) => n ?? "—",
        },
    {
      title: "Costo",
      dataIndex: "costoTransaccion",
      width: 110,
      align: "right" as const,
      render: (m: number | null) => (m != null ? CLP(m) : "—"),
    },
    {
      title: esCobro ? "Cobrado" : "Monto",
      dataIndex: "monto",
      width: 120,
      align: "right" as const,
      render: (m: number, r: FlujoRow) => {
        const delta = r.costoTransaccion != null ? m - r.costoTransaccion : null;
        return (
          <span>
            <Text strong>{CLP(m)}</Text>
            {esCobro && delta != null && delta !== 0 && (
              <div style={{ fontSize: 11 }}>
                <Text type={delta > 0 ? "success" : "danger"}>
                  {delta > 0 ? "+" : ""}
                  {CLP(delta)}
                </Text>
              </div>
            )}
          </span>
        );
      },
    },
    {
      title: "Estado",
      dataIndex: "estadoPago",
      width: 100,
      render: (e: string | null) =>
        e === "pendiente" ? (
          <Tag color="orange">Pendiente</Tag>
        ) : e === "pagado" ? (
          <Tag color="green">Pagado</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "",
      key: "acciones",
      width: 60,
      render: (_: any, r: FlujoRow) => (
        <Tooltip
          title={
            esCobro
              ? "Editar el monto cobrado al cliente (parcial / $0 = regalo / mayor)"
              : "Corregir el monto del reembolso"
          }
        >
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setFlujoEdit({ ...r, esCobro })}
          />
        </Tooltip>
      ),
    },
  ];

  const colsCategoria = [
    {
      title: "Categoría",
      dataIndex: "categoria",
      render: (c: string | null) =>
        c ? <Tag color="geekblue">{catLabel(c)}</Tag> : <Text type="secondary">(sin categoría)</Text>,
    },
    {
      title: (
        <span>
          Compras (egreso){" "}
          <Tooltip title="Suma de las compras del mes cuyos productos tienen esta categoría. Es la plata que salió de AF.">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      dataIndex: "compras",
      align: "right" as const,
      render: (m: number) => CLP(m),
    },
    {
      title: (
        <span>
          Cobros a clientes{" "}
          <Tooltip title="Suma de lo COBRADO a los clientes este mes por productos de esta categoría (monto editable — puede diferir del costo).">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      dataIndex: "cobros",
      align: "right" as const,
      render: (m: number) => CLP(m),
    },
    {
      title: (
        <span>
          Neto{" "}
          <Tooltip title="Cobros − Compras. Positivo = ganancia; negativo = AF puso plata (stock aún no cobrado, regalos o absorbido); 0 = neteado.">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      dataIndex: "neto",
      align: "right" as const,
      render: (m: number) => (
        <Text strong type={m > 0 ? "success" : m < 0 ? "danger" : undefined}>
          {CLP(m)}
        </Text>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <DatePicker
          picker="month"
          value={mes}
          onChange={(v) => v && setMes(v)}
          allowClear={false}
          format="MMMM YYYY"
        />
        <Button icon={<ReloadOutlined />} onClick={() => void cargar()}>
          Actualizar
        </Button>
        <Tooltip title="Toda la plata del inventario vive en esta pestaña (no en /flujos): las COMPRAS son el egreso de AF y quedan registradas el día de la compra; los COBROS al cliente nacen al entregar a una propiedad (independientes y editables); los REEMBOLSOS son adelantos de terceros. El desglose por categoría permite comparar qué pasó mensualmente con la plata de cada rubro.">
          <InfoCircleOutlined style={{ color: "#1677ff" }} />
        </Tooltip>
      </Space>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="No se pudieron cargar las finanzas de este mes"
          description={
            data
              ? `${error} Los números de abajo son del último mes que sí se pudo cargar (${data.mes}): NO corresponden al mes seleccionado.`
              : `${error} No hay datos que mostrar.`
          }
          action={
            <Button size="small" onClick={() => void cargar()} loading={cargando}>
              Reintentar
            </Button>
          }
        />
      )}
      {cargando && !data ? (
        <Spin tip="Cargando finanzas de inventario..." />
      ) : error && !data ? null : (
        <>
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title={
                    <span>
                      Compras del mes (egreso){" "}
                      <Tooltip title="Σ de las transacciones de compra del mes (totalCompras). La plata salió de AF el día de cada compra, sin importar a qué bodega/propiedad entró el stock.">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  value={CLP(resumen?.totalCompras)}
                  valueStyle={{ color: "#cf1322", fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title={
                    <span>
                      Cobros a clientes (ingreso){" "}
                      <Tooltip title="Σ de lo cobrado a los clientes este mes por productos entregados a sus propiedades. Cada cobro es editable (parcial / $0 = regalo / mayor al costo).">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  value={CLP(resumen?.totalRecuperaciones)}
                  valueStyle={{ color: "#3f8600", fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title={
                    <span>
                      Reembolsos a terceros{" "}
                      <Tooltip title="Adelantos que terceros (aseador/trabajador/etc.) hicieron por AF. NO es un egreso adicional (el costo ya está en Compras): registra a quién le debe AF. Pendientes: lo aún no pagado.">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  value={CLP(resumen?.totalReembolsos)}
                  valueStyle={{ fontSize: 20 }}
                />
                {(resumen?.reembolsosPendientes ?? 0) > 0 && (
                  <Text type="warning" style={{ fontSize: 12 }}>
                    Pendientes: {CLP(resumen?.reembolsosPendientes)}
                  </Text>
                )}
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title={
                    <span>
                      Neto del mes{" "}
                      <Tooltip title="Cobros a clientes − Compras. Positivo = el inventario dejó ganancia este mes; negativo = AF puso plata (stock comprado y aún no cobrado, regalos o costos absorbidos); 0 = neteado.">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  value={CLP(resumen?.neto)}
                  valueStyle={{
                    fontSize: 20,
                    color:
                      (resumen?.neto ?? 0) > 0
                        ? "#3f8600"
                        : (resumen?.neto ?? 0) < 0
                          ? "#cf1322"
                          : undefined,
                  }}
                />
              </Card>
            </Col>
          </Row>

          <Card
            size="small"
            title={
              <span>
                Por categoría{" "}
                <Tooltip title="Para qué existe la categoría del producto: entender mensualmente qué pasó con la plata de cada rubro (aseo, reparación, etc.). Los productos sin categoría se agrupan al final — asígnales una desde Editar producto.">
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            }
            style={{ marginBottom: 12 }}
          >
            <Table
              rowKey={(r: any) => r.categoria ?? "__sin__"}
              columns={colsCategoria}
              dataSource={data?.porCategoria ?? []}
              pagination={false}
              size="small"
              locale={{ emptyText: "Sin movimientos de inventario este mes" }}
            />
          </Card>

          <Tabs
            defaultActiveKey="transacciones"
            items={[
              {
                key: "transacciones",
                label: `Transacciones (${data?.transacciones.length ?? 0})`,
                children: (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 8 }}
                      message="Cada fila es un hecho histórico: una compra (egreso de AF en esa fecha) o un movimiento de stock. Editar el PRODUCTO no cambia estas filas; los errores de tipeo se corrigen aquí con el lápiz (con alerta de impacto), y la papelera elimina la transacción con sus flujos de dinero. Los movimientos entre bodegas van en $0 (el stock ya estaba pagado)."
                    />
                    <Table
                      rowKey="id"
                      columns={colsTx as any}
                      dataSource={data?.transacciones ?? []}
                      pagination={paginacionCliente({ porDefecto: 20, ocultarSiUnaPagina: true })}
                      size="small"
                      scroll={{ x: "max-content" }}
                      locale={{ emptyText: "Sin transacciones este mes" }}
                    />

                    {/* Buscador global de transacciones sin producto. Esta
                        vista es MENSUAL: sin esto hay que acordarse de en qué
                        mes quedó cada una para poder verla. */}
                    <div style={{ marginTop: 16 }}>
                      <Space wrap>
                        <Button
                          icon={<SearchOutlined />}
                          loading={buscandoHuerfanas}
                          onClick={() => void buscarHuerfanas()}
                        >
                          Buscar transacciones sin producto (todos los meses)
                        </Button>
                        {huerfanas && (
                          <Text type="secondary">
                            {huerfanas.total === 0
                              ? "No hay ninguna."
                              : `${huerfanas.total} encontrada(s), ${CLP(huerfanas.montoTotal)} en total.`}
                          </Text>
                        )}
                      </Space>

                      {huerfanas && huerfanas.total > 0 && (
                        <>
                          <Alert
                            type="warning"
                            showIcon
                            style={{ marginTop: 12, marginBottom: 8 }}
                            message="Estas transacciones perdieron su producto"
                            description="Siguen contando como gasto del mes en que ocurrieron, pero no aparecen en la vista Productos. Antes de borrarlas: si el stock se movió a otra ubicación, la mercadería EXISTE y esta fila es el único registro de lo que costó — bórrala solo si esa compra no debió existir."
                          />
                          <Table
                            rowKey="id"
                            size="small"
                            scroll={{ x: "max-content" }}
                            pagination={paginacionCliente({ porDefecto: 10, ocultarSiUnaPagina: true })}
                            dataSource={huerfanas.transacciones ?? []}
                            locale={{
                              emptyText: (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                              ),
                            }}
                            columns={
                              [
                                {
                                  title: "Código",
                                  key: "codigos",
                                  width: 118,
                                  render: (_: any, r: any) => (
                                    <Space direction="vertical" size={0}>
                                      <Tag color="default" className="inventario-id-tag">
                                        TX #{r.id}
                                      </Tag>
                                      <Tag color="red" className="inventario-id-tag">
                                        PROD #{r.inventarioId ?? "?"}
                                      </Tag>
                                    </Space>
                                  ),
                                },
                                { title: "Fecha", dataIndex: "fecha", width: 105 },
                                {
                                  title: "Tipo",
                                  dataIndex: "tipo",
                                  width: 110,
                                  render: (t: string) =>
                                    t === "compra" ? (
                                      <Tag color="volcano">Compra</Tag>
                                    ) : (
                                      <Tag color="blue">Movimiento</Tag>
                                    ),
                                },
                                { title: "Producto", dataIndex: "productoNombre" },
                                {
                                  title: "Monto",
                                  dataIndex: "montoTotal",
                                  width: 120,
                                  align: "right" as const,
                                  render: (m: number) => <Text strong>{CLP(m)}</Text>,
                                },
                                {
                                  title: "Flujos vinculados",
                                  key: "flujos",
                                  width: 170,
                                  render: (_: any, r: any) =>
                                    r.cobrosVinculados + r.reembolsosVinculados === 0 ? (
                                      <Text type="secondary">—</Text>
                                    ) : (
                                      <Text style={{ fontSize: 12 }}>
                                        {r.cobrosVinculados} cobro(s),{" "}
                                        {r.reembolsosVinculados} reembolso(s) ·{" "}
                                        {CLP(r.montoFlujos)}
                                      </Text>
                                    ),
                                },
                                {
                                  title: "",
                                  key: "acciones",
                                  width: 100,
                                  render: (_: any, r: any) => (
                                    <Space size={4}>
                                      {/* La historia del producto sigue
                                          disponible aunque el producto ya no
                                          exista: es lo que permite entender si
                                          esta compra es basura o el único
                                          registro de una mercadería que sí
                                          está, en otra ubicación. */}
                                      <Tooltip title="Ver la historia de este producto">
                                        <Button
                                          size="small"
                                          icon={<HistoryOutlined />}
                                          disabled={r.inventarioId == null}
                                          onClick={() =>
                                            setHistoria({
                                              id: Number(r.inventarioId),
                                              nombre: r.productoNombre,
                                            })
                                          }
                                        />
                                      </Tooltip>
                                      <Tooltip title="Eliminar esta transacción y sus flujos vinculados">
                                        <Button
                                          size="small"
                                          danger
                                          icon={<DeleteOutlined />}
                                          onClick={() => setTxDelete(r as TxRow)}
                                        />
                                      </Tooltip>
                                    </Space>
                                  ),
                                },
                              ] as any
                            }
                          />
                        </>
                      )}

                      {/* ── El daño INVERSO ──────────────────────────────
                          Un cobro/reembolso cuya transacción de inventario ya
                          no existe. Se generaba al borrar una propiedad: la
                          transacción moría y el flujo no, porque ningún
                          criterio del borrado lo alcanzaba. Quedaba invisible
                          (/flujos oculta los derivados de inventario) e
                          indestructible (su borrado respondía 409), pero
                          seguía sumando en los reportes y apareciendo como
                          deuda pendiente en el panel del aseador.
                          Round 2026-08-06. */}
                      {huerfanas?.resumenFlujos?.total > 0 && (
                        <>
                          <Alert
                            type="error"
                            showIcon
                            style={{ marginTop: 16, marginBottom: 8 }}
                            message={`${huerfanas.resumenFlujos.total} cobro(s)/reembolso(s) sin transacción — ${CLP(huerfanas.resumenFlujos.monto)}`}
                            description={
                              huerfanas.resumenFlujos.pendientes > 0
                                ? `Su compra de inventario ya no existe, así que no se pueden gestionar desde ninguna otra pantalla. ${huerfanas.resumenFlujos.pendientes} está(n) PENDIENTE(S): alguien los ve como plata que se le debe por una compra que ya no está registrada.`
                                : "Su compra de inventario ya no existe, así que no se pueden gestionar desde ninguna otra pantalla. Siguen sumando en los reportes."
                            }
                          />
                          <Table
                            rowKey="id"
                            size="small"
                            scroll={{ x: "max-content" }}
                            pagination={paginacionCliente({ porDefecto: 10, ocultarSiUnaPagina: true })}
                            dataSource={huerfanas.flujosSinTransaccion ?? []}
                            columns={
                              [
                                { title: "Fecha", dataIndex: "fecha", width: 105 },
                                {
                                  title: "Tipo",
                                  dataIndex: "rol",
                                  width: 120,
                                  render: (rol: string) =>
                                    rol === "recibe" ? (
                                      <Tag color="blue">Cobro al cliente</Tag>
                                    ) : (
                                      <Tag color="purple">Reembolso</Tag>
                                    ),
                                },
                                {
                                  title: "Contraparte",
                                  dataIndex: "contraparteNombre",
                                  render: (v: string | null) =>
                                    v || <Text type="secondary">—</Text>,
                                },
                                {
                                  title: "Monto",
                                  dataIndex: "monto",
                                  width: 120,
                                  align: "right" as const,
                                  render: (m: number) => <Text strong>{CLP(m)}</Text>,
                                },
                                {
                                  title: "Estado",
                                  dataIndex: "estadoPago",
                                  width: 110,
                                  render: (e: string | null) =>
                                    e === "pendiente" ? (
                                      <Tag color="orange">Pendiente</Tag>
                                    ) : (
                                      <Tag>{e ?? "—"}</Tag>
                                    ),
                                },
                                {
                                  title: "",
                                  key: "acciones",
                                  width: 60,
                                  render: (_: any, r: any) => (
                                    <Popconfirm
                                      title="Eliminar este flujo"
                                      description={
                                        <div style={{ maxWidth: 320 }}>
                                          Su transacción de inventario ya no
                                          existe, así que no hay contabilidad de
                                          producto que mantener coherente. Se
                                          elimina también su par, si tiene.
                                        </div>
                                      }
                                      okText="Eliminar"
                                      okButtonProps={{ danger: true }}
                                      cancelText="Cancelar"
                                      onConfirm={() =>
                                        void eliminarFlujoHuerfano(Number(r.id))
                                      }
                                    >
                                      <Tooltip title="Eliminar este flujo sin transacción">
                                        <Button
                                          size="small"
                                          danger
                                          icon={<DeleteOutlined />}
                                        />
                                      </Tooltip>
                                    </Popconfirm>
                                  ),
                                },
                              ] as any
                            }
                          />
                        </>
                      )}
                    </div>
                  </>
                ),
              },
              {
                key: "cobros",
                label: `Cobros a clientes (${data?.recuperaciones.length ?? 0})`,
                children: (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 8 }}
                      message="Lo que se le cobra al cliente al ENTREGAR un producto a su propiedad. Nace igual al costo pero es INDEPENDIENTE e editable: parcial, mayor (ganancia) o $0 (regalo). La columna Cobrado muestra el delta contra el costo."
                    />
                    <Table
                      rowKey="id"
                      columns={colsFlujo(true) as any}
                      dataSource={data?.recuperaciones ?? []}
                      pagination={paginacionCliente({ porDefecto: 20, ocultarSiUnaPagina: true })}
                      size="small"
                      scroll={{ x: "max-content" }}
                      locale={{ emptyText: "Sin cobros a clientes este mes" }}
                    />
                  </>
                ),
              },
              {
                key: "reembolsos",
                label: `Reembolsos (${data?.reembolsos.length ?? 0})`,
                children: (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 8 }}
                      message="Adelantos que un tercero hizo por AF al comprar. No suman como egreso extra (el costo ya está en Compras); registran la deuda con esa persona. El aseador/trabajador los ve en su propio panel; la aprobación de pago se gestiona allá."
                    />
                    <Table
                      rowKey="id"
                      columns={colsFlujo(false) as any}
                      dataSource={data?.reembolsos ?? []}
                      pagination={paginacionCliente({ porDefecto: 20, ocultarSiUnaPagina: true })}
                      size="small"
                      scroll={{ x: "max-content" }}
                      locale={{ emptyText: "Sin reembolsos este mes" }}
                    />
                  </>
                ),
              },
            ]}
          />
        </>
      )}

      <TxEditModal
        tx={txEdit}
        onClose={(recargar) => {
          setTxEdit(null);
          if (recargar) void cargar();
        }}
      />
      <TxDeleteModal
        tx={txDelete}
        onClose={(recargar) => {
          setTxDelete(null);
          if (recargar) {
            void cargar();
            // Si el listado global de huérfanas está abierto, se re-escanea:
            // la fila recién borrada tiene que desaparecer de ahí también.
            if (huerfanas) void buscarHuerfanas();
          }
        }}
      />
      <MontoEditModal
        flujo={flujoEdit}
        onClose={(recargar) => {
          setFlujoEdit(null);
          if (recargar) void cargar();
        }}
      />
      {/* Línea de tiempo del producto — se abre desde cualquier transacción,
          incluidas las huérfanas (el endpoint acepta ids de productos ya
          borrados mientras quede rastro). Round 2026-08-06. */}
      <HistoriaProductoDrawer
        inventarioId={historia?.id ?? null}
        nombreFallback={historia?.nombre ?? null}
        open={historia != null}
        onClose={() => setHistoria(null)}
      />
    </div>
  );
};

export default InventarioFinanzasTab;
